// src/features/intervene/hooks/useUserList.js
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  interveneUserListAtom,
  interveneFilterAtom,
  interveneChatCountsAtom,
  interveneUserDetailAtom,
  interveneMessageListAtom,
} from '../state/interveneAtoms';
import { interveneService } from '../services/interveneService';
import useChatCounts from './useChatCounts';
import {
  markUserReadInList,
  normalizeUserListResponse,
} from '../utils/interveneApiAdapters';
import { chatDebug } from '../utils/chatDebug';
import SweetAlert from '../../../components/Shared/Notification';
import { interveneSocketConnectionAtom } from '../sockets/state/socketAtoms';
import { buildUserSelectionState } from '../utils/userSelectionState';
import { userInformationAtom } from '../../../Services/_states';
import {
  applyInterveneOrganisationScope,
  getInterveneCompanyId,
  lockInterveneOrganisationForUser,
} from '../utils/interveneOrganisationScope';

export default function useUserList() {
  const userInfo = useRecoilValue(userInformationAtom);
  const [userList, setUserList] = useRecoilState(interveneUserListAtom);
  const [filters, setFilters] = useRecoilState(interveneFilterAtom);
  const [chatCounts, setChatCounts] = useRecoilState(interveneChatCountsAtom);
  const socketConnection = useRecoilValue(interveneSocketConnectionAtom);

  const setUserDetail = useSetRecoilState(interveneUserDetailAtom);
  const setMessageList = useSetRecoilState(interveneMessageListAtom);
  const { refreshChatCounts } = useChatCounts();

  const [isLoading, setIsLoading] = useState(false);
  const requestIdRef = useRef(0);
  const filtersRef = useRef(filters);
  const userDetailRef = useRef({});
  filtersRef.current = filters;
  const scopedFilters = useMemo(
    () => applyInterveneOrganisationScope(filters, userInfo),
    [filters, userInfo]
  );

  useEffect(() => {
    setFilters((prev) => {
      const next = applyInterveneOrganisationScope(prev, userInfo);
      if (next.organisationId === prev.organisationId) {
        return prev;
      }
      return next;
    });
  }, [userInfo, setFilters]);

  const prevSocketStatusRef = useRef({
    chat: 'Disconnected',
    notificationsBridge: 'Disconnected',
  });
  const wasEverConnectedRef = useRef(false);

  const loadUserList = useCallback(async (currentFilters, resetList = false) => {
    const requestId = ++requestIdRef.current;
    setIsLoading(true);

    try {
      const scoped = applyInterveneOrganisationScope(currentFilters, userInfo);
      const companyId = getInterveneCompanyId(scoped, userInfo);

      const res = await interveneService.getUserList({
        ...scoped,
        company_id: companyId,
      });
      if (requestId !== requestIdRef.current) return;

      if (res.status === 200) {
        const normalized = normalizeUserListResponse(res);
        setUserList((prev) => ({
          users: resetList ? normalized.list : [...prev.users, ...normalized.list],
          total_count: normalized.count,
          has_next: normalized.hasNext,
        }));
      }
    } catch (err) {
      if (requestId === requestIdRef.current) {
        SweetAlert(err.message || 'Error fetching users', 'error');
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [setUserList, userInfo]);

  // Re-sync list once after socket reconnect (not on every message).
  useEffect(() => {
    const prev = prevSocketStatusRef.current;
    const wasConnected =
      prev.chat === 'Connected' || prev.notificationsBridge === 'Connected';
    const isConnected =
      socketConnection.chat === 'Connected' ||
      socketConnection.notificationsBridge === 'Connected';

    const reconnected = isConnected && !wasConnected && wasEverConnectedRef.current;

    if (isConnected) {
      wasEverConnectedRef.current = true;
    }

    prevSocketStatusRef.current = {
      chat: socketConnection.chat,
      notificationsBridge: socketConnection.notificationsBridge,
    };

    if (!reconnected) return;

    const current = filtersRef.current;
    if (!current?.botNumber || current?.search) return;

    loadUserList({ ...current, page: 1 }, true);
    refreshChatCounts(
      current.botNumber,
      getInterveneCompanyId(applyInterveneOrganisationScope(current, userInfo), userInfo)
    );
  }, [socketConnection, loadUserList, refreshChatCounts, userInfo]);

  useEffect(() => {
    if (scopedFilters.botNumber) {
      refreshChatCounts(
        scopedFilters.botNumber,
        getInterveneCompanyId(scopedFilters, userInfo)
      );
      return;
    }
    setChatCounts({ intervene: 0, min_15: 0, hrs_24: 0, history: 0, total: 0 });
  }, [scopedFilters.botNumber, scopedFilters.organisationId, filters.userType, refreshChatCounts, setChatCounts, userInfo]);

  useEffect(() => {
    if (!scopedFilters.botNumber) {
      setUserList({ users: [], total_count: 0, has_next: false });
      return;
    }
    loadUserList(scopedFilters, true);
  }, [filters.userType, scopedFilters.botNumber, scopedFilters.organisationId, filters.search, loadUserList]);

  const loadUserListPage = loadUserList;

  const handleTabChange = (newTabValue) => {
    switchToTab(newTabValue, { preserveActiveChat: false });
  };

  const switchToTab = useCallback((newTabValue, { preserveActiveChat = false } = {}) => {
    if (newTabValue === filters.userType) return false;

    setUserList({ users: [], total_count: 0, has_next: false });

    if (!preserveActiveChat) {
      setUserDetail({});
      setMessageList({
        messages: [],
        total_count: 0,
        filters: {
          page: 1,
          phone_number: '',
          botNumber: '',
        },
      });
    }

    setFilters((prev) => lockInterveneOrganisationForUser({
      ...prev,
      userType: newTabValue,
      page: 1,
      search: '',
    }, userInfo));

    return true;
  }, [filters.userType, setFilters, setMessageList, setUserDetail, setUserList, userInfo]);

  const switchToTabPreservingChat = useCallback(
    (newTabValue) => switchToTab(newTabValue, { preserveActiveChat: true }),
    [switchToTab]
  );

  const handleSearch = (searchText) => {
    setFilters((prev) => lockInterveneOrganisationForUser(
      { ...prev, search: searchText, page: 1 },
      userInfo
    ));
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const canLoadMore =
      userList.has_next || userList.total_count > userList.users.length;

    if (scrollTop + clientHeight >= scrollHeight - 50) {
      if (canLoadMore && !isLoading) {
        const nextPage = filters.page + 1;
        setFilters((prev) => lockInterveneOrganisationForUser({ ...prev, page: nextPage }, userInfo));
        loadUserListPage({ ...scopedFilters, page: nextPage }, false);
      }
    }
  };

  const markUserAsReadLocally = useCallback((phoneNumber) => {
    if (!phoneNumber) return;
    setUserList((prev) => ({
      ...prev,
      users: markUserReadInList(prev.users, phoneNumber),
    }));
  }, [setUserList]);

  const handleUserSelect = (user) => {
    if (user.phone_number) {
      chatDebug('user-selected-reset-chat', {
        selectedUser: {
          phone_number: user.phone_number,
          whatsapp_name: user.whatsapp_name,
          updated_at: user.updated_at,
          last_message: {
            id: user.last_message?.id || user.last_message?.msg_id || user.last_message?.w_id,
            phone_number: user.last_message?.phone_number,
            created_at: user.last_message?.created_at,
            type: user.last_message?.content?.type || user.last_message?.type,
          },
        },
        botNumber: filters.botNumber,
        previousFilters: filters,
      });

      const nextUserDetail = buildUserSelectionState(user, userDetailRef.current || {}, filters.botNumber);
      userDetailRef.current = nextUserDetail;
      setUserDetail(nextUserDetail);
      setMessageList((prev) => ({
        ...prev,
        filters: {
          page: 1,
          phone_number: user.phone_number,
          botNumber: filters.botNumber,
        },
      }));
      markUserAsReadLocally(user.phone_number);
    }
  };

  return {
    users: userList.users,
    totalCount: userList.total_count,
    hasNextPage: userList.has_next,
    chatCounts,
    filters,
    isLoading,
    handleTabChange,
    switchToTabPreservingChat,
    handleSearch,
    handleScroll,
    handleUserSelect,
    refreshChatCounts,
  };
}
